'use client';
import { propertiesByEvent } from '@/constants/properties-by-event';
import { QueryKeys } from '@/constants/query-keys';
import { AnalyticsEvent, AnalyticsView } from '@/enums/analytics-type';
import { clientErrorLoggerApiService } from '@/services/(frontend)/client-error-logger-api.service';
import { AnalyticsBaseType } from '@/types/analytics';
import { convertKeysToSnakeCase, snakeToCamel } from '@/utils/camel-caser';
import { globalWindow } from '@/utils/global-window';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAnalyticsDataLayer, useAnalyticsReady } from './analytics';
import { useAnalyticsLog } from './analytics-log';

const AnalyticsQueueStorageKey = 'cinemark_analytics_queue';
const QueueMaxAgeMs = 24 * 60 * 60 * 1000; // 24 hours

type AnalyticsQueueItem = {
  data: Record<string, AnalyticsBaseType>;
  date: string;
  eventName: AnalyticsEvent | AnalyticsView;
};

type UseDataAnalyticsActionsType = {
  data: Omit<Record<string, AnalyticsBaseType>, 'tealiumEvent'>;
  eventName: AnalyticsEvent | AnalyticsView;
};

const encodeBase64 = (data: string): string => {
  if (typeof window === 'undefined') return data;

  try {
    return btoa(data);
  } catch {
    return data;
  }
};

const decodeBase64 = (encodedData: string): string => {
  if (typeof window === 'undefined') return encodedData;

  try {
    return atob(encodedData);
  } catch {
    return encodedData;
  }
};

const clearAnalyticsQueueStorage = (): void => {
  if (typeof window === 'undefined') return;

  localStorage.removeItem(AnalyticsQueueStorageKey);
};

const loadQueueFromStorage = (): AnalyticsQueueItem[] => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = localStorage.getItem(AnalyticsQueueStorageKey);
    if (!stored) return [];

    const decodedData = decodeBase64(stored);
    const serializedQueue: AnalyticsQueueItem[] = JSON.parse(decodedData);
    const now = Date.now();

    return serializedQueue.filter(item => now - new Date(item.date).getTime() < QueueMaxAgeMs);
  } catch {
    clearAnalyticsQueueStorage();
    return [];
  }
};

const saveQueueToStorage = (queue: AnalyticsQueueItem[]): void => {
  if (typeof window === 'undefined') return;

  if (!queue.length) {
    clearAnalyticsQueueStorage();
    return;
  }

  try {
    const jsonData = JSON.stringify(queue);
    const encodedData = encodeBase64(jsonData);

    localStorage.setItem(AnalyticsQueueStorageKey, encodedData);
  } catch {
    clearAnalyticsQueueStorage();
  }
};

export const useAnalyticsQueue = (): ReturnType<typeof useQuery<AnalyticsQueueItem[]>> => {
  return useQuery<AnalyticsQueueItem[]>({
    initialData: loadQueueFromStorage(),
    queryFn: () => loadQueueFromStorage(),
    queryKey: QueryKeys.global.analyticsQueue,
    staleTime: Infinity,
  });
};

export const useEnqueueAnalyticsItem = (): ReturnType<typeof useMutation<void, unknown, UseDataAnalyticsActionsType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, eventName }: UseDataAnalyticsActionsType) => {
      const item: AnalyticsQueueItem = { data, date: new Date().toISOString(), eventName };

      queryClient.setQueryData<AnalyticsQueueItem[]>(
        QueryKeys.global.analyticsQueue,
        (prevQueue = []) => {
          const newQueue = [...prevQueue, item];
          saveQueueToStorage(newQueue);
          return newQueue;
        }
      );

      return Promise.resolve();
    },
  });
};

const dequeueAnalyticsItem = (queryClient: ReturnType<typeof useQueryClient>): AnalyticsQueueItem | null => {
  let dequeuedItem: AnalyticsQueueItem | null = null;

  queryClient.setQueryData<AnalyticsQueueItem[]>(
    QueryKeys.global.analyticsQueue,
    (prevQueue = []) => {
      if (!prevQueue.length) {
        return prevQueue;
      }

      const [firstItem, ...remainingItems] = prevQueue;
      dequeuedItem = firstItem;
      saveQueueToStorage(remainingItems);
      return remainingItems;
    }
  );

  return dequeuedItem;
};

export const useAutoProcessAnalyticsQueue = (): void => {
  const queryClient = useQueryClient();
  const { data: queue = [] } = useAnalyticsQueue();
  const { data: analyticsDataLayer, isLoading: loadingAnalytics } = useAnalyticsDataLayer();
  const { data: isAnalyticsReady } = useAnalyticsReady();
  const { mutateAsync: updateAnalyticsLog } = useAnalyticsLog();

  useEffect(() => {
    const processAnalyticsItem = async (item: AnalyticsQueueItem): Promise<void> => {
      const { data, eventName } = item;

      if (!globalWindow?.utag) {
        return;
      }

      const newData: Record<string, AnalyticsBaseType> = { ...data, tealiumEvent: eventName };

      try {
        const camelCaseEventName = snakeToCamel(eventName);
        const propertiesToTrack = camelCaseEventName in propertiesByEvent
          ? propertiesByEvent[camelCaseEventName as keyof typeof propertiesByEvent]
          : [];

        if (propertiesToTrack?.length && analyticsDataLayer) {
          propertiesToTrack.forEach((key) => {
            const value = analyticsDataLayer[key];
            if (newData[key] === undefined && value !== undefined) {
              newData[key] = value;
            }
          });
        }

        const snakeCaseData = convertKeysToSnakeCase(newData) as Record<string, AnalyticsBaseType>;

        if (eventName === AnalyticsEvent.DATA_LAYER && globalWindow.utag.data) {
          const dataLayerData = Object.fromEntries(
            Object.entries(snakeCaseData).filter(([key]) => key !== 'tealium_event')
          );
          Object.assign(globalWindow.utag.data, dataLayerData);
        } else if (Object.values(AnalyticsEvent).includes(eventName as AnalyticsEvent) && globalWindow.utag.link) {
          globalWindow.utag.link(snakeCaseData);
          await updateAnalyticsLog(snakeCaseData);
        } else if (Object.values(AnalyticsView).includes(eventName as AnalyticsView) && globalWindow.utag.view) {
          globalWindow.utag.view(snakeCaseData);
          if (globalWindow.utag.data) {
            Object.assign(globalWindow.utag.data, snakeCaseData);
          }
          await updateAnalyticsLog(snakeCaseData);
        } else {
          void clientErrorLoggerApiService.logError(new Error('Unknown analytics event encountered'));
        }
      } catch (error) {
        void clientErrorLoggerApiService.logError(error);
      }
    };

    const isReadyForDispatch = isAnalyticsReady || !!globalWindow?.utag;
    if (isReadyForDispatch && globalWindow?.utag && !loadingAnalytics && queue.length) {
      const item = dequeueAnalyticsItem(queryClient);

      if (item) {
        processAnalyticsItem(item);
      }
    }
  }, [analyticsDataLayer, isAnalyticsReady, loadingAnalytics, queue, queryClient, updateAnalyticsLog]);
};
